import { fontAttrs } from "@/components/tools/printables/page-kit";
import type { PlacedText } from "@/lib/tool-kit/printables/reward-chart";
import type {
  PlannerLine,
  PlannerPage,
} from "@/lib/tool-kit/study-more/weekly-planner";

const INK = "#1a1a1a";
const RULE = "#6b6b6b";
const BOLD = { kind: "sans", bold: true } as const;
const SANS = { kind: "sans" } as const;

const STROKE: Record<PlannerLine["weight"], { color: string; width: number }> =
  {
    heavy: { color: INK, width: 0.5 },
    normal: { color: RULE, width: 0.25 },
    faint: { color: "#b8b8b8", width: 0.2 },
  };

function Label({ item, fill }: { item: PlacedText; fill: string }) {
  return (
    <text
      fill={fill}
      fontSize={item.size}
      textAnchor={item.anchor}
      x={item.x}
      y={item.y}
      {...fontAttrs(item.bold ? BOLD : SANS)}
    >
      {item.text}
    </text>
  );
}

/** One planner sheet, drawn in millimetres. */
export function PlannerPageArt({
  page,
  accent,
  accentFirst = true,
}: {
  page: PlannerPage;
  accent: string;
  /** Colour the first text (the page title) in the accent. */
  accentFirst?: boolean;
}) {
  return (
    <g>
      {page.boxes.map((box) => (
        <rect
          fill="none"
          height={box.height}
          key={`${box.x}-${box.y}-${box.width}`}
          rx={0.8}
          stroke={STROKE.heavy.color}
          strokeWidth={0.4}
          width={box.width}
          x={box.x}
          y={box.y}
        />
      ))}
      {page.lines.map((item) => (
        <line
          key={`${item.x1}-${item.y1}-${item.x2}-${item.y2}`}
          stroke={STROKE[item.weight].color}
          strokeDasharray={item.dashed ? "1 1" : undefined}
          strokeWidth={STROKE[item.weight].width}
          x1={item.x1}
          x2={item.x2}
          y1={item.y1}
          y2={item.y2}
        />
      ))}
      {page.checks.map((box) => (
        <rect
          fill="none"
          height={box.height}
          key={`${box.x}-${box.y}`}
          stroke={RULE}
          strokeWidth={0.3}
          width={box.width}
          x={box.x}
          y={box.y}
        />
      ))}
      {page.texts.map((item, index) => (
        <Label
          fill={index === 0 && accentFirst ? accent : INK}
          item={item}
          key={`${item.x}-${item.y}-${item.text}`}
        />
      ))}
    </g>
  );
}
